/**
 * The single WebSocket client (M2.2): one socket per client to the owning actor,
 * carrying both commands (client→server) and the state stream (server→client).
 *
 * Responsibilities:
 *   - open the socket for a validated ticket and send `hello` (handshake + resync);
 *   - feed every inbound event into the {@link GameStore};
 *   - on a version gap the store signals, re-send `hello` for a full resync
 *     (DESIGN §5.3) — the store never applies a delta onto stale state;
 *   - auto-reconnect with capped backoff, re-`hello`-ing on reconnect (NFR-4.2).
 *
 * Injectable socket factory + timer/reconnect/visibility ports keep it unit-testable
 * without a browser, and let a non-browser client (React Native, #104) reuse it verbatim.
 */

import {
  JEOPARDY_MODE_VERSION,
  PROTOCOL_VERSION,
  type BuzzerOpenScheduledEvent,
  type Command,
  type GameModeId,
  type ServerEvent,
} from '@jeopardy/shared';
import { ClockSyncEstimator, toClientTime, type SyncState } from '@jeopardy/clock-sync';
import { GameStore } from './game-store';

/** Minimal structural type for the parts of WebSocket this client uses. */
export interface SocketLike {
  send(data: string): void;
  close(): void;
  onopen: ((this: unknown, ev: unknown) => unknown) | null;
  onclose: ((this: unknown, ev: unknown) => unknown) | null;
  onerror: ((this: unknown, ev: unknown) => unknown) | null;
  onmessage: ((this: unknown, ev: { data: unknown }) => unknown) | null;
  readonly readyState: number;
}

export type SocketFactory = (url: string) => SocketLike;

/**
 * #264 (D-264.1): the fast sampling cadence (ms) used while the clock estimate is not yet
 * trustworthy (grade C / suspect / too few samples), so a client warms a usable offset within
 * ~1 second of entering play. Clamped never to exceed the server steady interval. Once the
 * estimate reaches grade A/B the loop backs off to that steady cadence.
 */
export const WARM_SYNC_INTERVAL_MS = 400;

/**
 * #264 (FR-4.13, D-264.5): the never-early client bias. The local buzzer-unlock deadline is
 * `toClientTime(OPEN_AT) + safetyMargin`, where the margin is the client's own offset
 * uncertainty clamped to this cap — so a synced client never unlocks BEFORE the server's
 * `OPEN_AT` acceptance window (early buzzes stay governed by FR-4.3), while the cap keeps a
 * transiently-huge uncertainty from delaying the open by more than a beat.
 */
export const MAX_SCHEDULED_OPEN_SAFETY_MARGIN_MS = 50;

export interface WsClientOptions {
  url: string;
  store: GameStore;
  /**
   * The game's mode (#257 S3), learned from the ticket/join REST `gameType`. Sent on `hello`
   * so the server can negotiate the per-mode sub-contract. Defaults to `JEOPARDY` (the only
   * mode today); S4 threads the real value from the join response.
   */
  gameType?: GameModeId;
  /** Defaults to the browser `WebSocket`. Injectable for tests. */
  socketFactory?: SocketFactory;
  /** Reconnect base delay (ms); backoff caps at 10× this. Default 500. */
  reconnectBaseMs?: number;
  /** Schedule a delayed reconnect. Defaults to setTimeout. Injectable for tests. */
  scheduleReconnect?: (fn: () => void, ms: number) => void;
  /** Called when the connection opens (for connection-status UI). */
  onOpenChange?: (open: boolean) => void;
  /**
   * #247 (NFR-6.7): monotonic clock for clock-sync. Defaults to `performance.now()`.
   * Injectable for deterministic tests.
   */
  now?: () => number;
  /** #247: interval scheduler for the sync loop (defaults to `setInterval`). */
  setIntervalFn?: (fn: () => void, ms: number) => ReturnType<typeof setInterval>;
  /** #247: interval clearer for the sync loop (defaults to `clearInterval`). */
  clearIntervalFn?: (handle: ReturnType<typeof setInterval>) => void;
  /**
   * #247: register a page-visibility-change listener; the returned fn unregisters it. The
   * client flags the sync estimate `suspect` on hidden→visible (a throttled tab's timers
   * skew sampling). Defaults to `document.visibilitychange`; injectable/absent in tests.
   */
  registerVisibility?: (cb: () => void) => () => void;
  /**
   * Keepalive `ping` interval (ms). While connected, send a `ping` this often so an
   * IDLE connection (a quiet lobby, the gap between clues, a demo waiting for its box)
   * stays under the load balancer's 60s idle timeout — otherwise the ALB drops a silent
   * socket at 60s and a server-driven state change (e.g. the demo time box firing at
   * 120s, #246) never reaches the client until a later reconnect. Default 25000; a
   * value ≤ 0 disables it. Injectable interval scheduler for tests.
   */
  keepAliveMs?: number;
  /** Schedule a repeating keepalive. Defaults to setInterval. Injectable for tests. */
  scheduleKeepAlive?: (fn: () => void, ms: number) => () => void;
  /**
   * Called once per successful *reconnect* (socket reopened after ≥1 backoff attempt),
   * with the attempt count that finally succeeded. A metrics/telemetry port so this
   * package stays platform-agnostic: the web client wires it to RUM's `recordWsReconnect`
   * (EI-4 / #35); native / tests pass nothing (default no-op). Not called on the first
   * connect. Fire-and-forget — must not throw.
   */
  onReconnect?: (info: { attempt: number }) => void;
  /**
   * #264 (FR-4.13): called when the client's LOCAL scheduled buzzer open reaches its instant
   * (with the `clueId`) and when it clears (`null`, on supersede / reconnect). The UI ORs
   * `clueId === current clueState.clueId` into `canBuzz` so a synced player's buzzer unlocks at
   * the shared `OPEN_AT` — ahead of the authoritative `buzzer_opened` frame's delivery — WITHOUT
   * disturbing the early-smash / reopened-buzz gates (those stay on `buzzerWindowOpen`, S4). No-op
   * on the fallback path (no usable sync / late announcement), where the buzzer opens on
   * `buzzer_opened` as before. Fire-and-forget — must not throw.
   */
  onScheduledOpenChange?: (clueId: string | null) => void;
  /** #264: one-shot timer for the local scheduled open. Defaults to setTimeout. Injectable. */
  setTimeoutFn?: (fn: () => void, ms: number) => ReturnType<typeof setTimeout>;
  /** #264: clearer for the scheduled-open timer. Defaults to clearTimeout. Injectable. */
  clearTimeoutFn?: (handle: ReturnType<typeof setTimeout>) => void;
}

const OPEN = 1;

export class WsClient {
  private socket: SocketLike | null = null;
  private closedByUs = false;
  private reconnectAttempts = 0;
  private readonly reconnectBaseMs: number;
  private readonly scheduleReconnect: (fn: () => void, ms: number) => void;
  private readonly keepAliveMs: number;
  private readonly scheduleKeepAlive: (fn: () => void, ms: number) => () => void;
  /** Cancels the active keepalive interval; null when not connected. */
  private cancelKeepAlive: (() => void) | null = null;

  // #247 (NFR-6.7): clock-sync state. The loop runs while the server enables it via
  // `sync_config` — #264 (D-264.1) enables it for every game in active play (not only
  // observed ones), so no steady sync traffic on lobby/idle games.
  private readonly now: () => number;
  private readonly setIntervalFn: (
    fn: () => void,
    ms: number,
  ) => ReturnType<typeof setInterval>;
  private readonly clearIntervalFn: (handle: ReturnType<typeof setInterval>) => void;
  private readonly estimator: ClockSyncEstimator;
  private syncTimer: ReturnType<typeof setInterval> | null = null;
  private syncEnabled = false;
  /**
   * #264 (D-264.1): warm-then-slow cadence. Un-gating sync (it now runs for every in-play
   * game, not just observed ones) means thousands of idle-but-in-play sockets would carry
   * steady traffic — so the loop samples FAST while the estimate is untrustworthy (grade C /
   * suspect / too few samples), then backs off to the server-provided steady interval once
   * the estimate is A/B. `slowSyncIntervalMs` is that steady cadence; `currentSyncIntervalMs`
   * is what the timer is currently armed at, so {@link maybeReArmSyncCadence} only re-arms on
   * a tier change.
   */
  private slowSyncIntervalMs = 0;
  private currentSyncIntervalMs = 0;
  /** The clueId we last sent a `buzzer_open_ack` for (dedupe one ack per open). */
  private lastAckedOpenClueId: string | null = null;
  private readonly unregisterVisibility: (() => void) | null = null;
  // #264 (FR-4.13): the local scheduled buzzer-open timer + the clue it's armed for.
  private readonly setTimeoutFn: (
    fn: () => void,
    ms: number,
  ) => ReturnType<typeof setTimeout>;
  private readonly clearTimeoutFn: (handle: ReturnType<typeof setTimeout>) => void;
  private scheduledOpenTimer: ReturnType<typeof setTimeout> | null = null;
  private scheduledOpenClueId: string | null = null;

  constructor(private readonly opts: WsClientOptions) {
    this.reconnectBaseMs = opts.reconnectBaseMs ?? 500;
    this.scheduleReconnect =
      opts.scheduleReconnect ?? ((fn, ms) => void setTimeout(fn, ms));
    this.now = opts.now ?? (() => performance.now());
    this.setIntervalFn = opts.setIntervalFn ?? ((fn, ms) => setInterval(fn, ms));
    this.clearIntervalFn = opts.clearIntervalFn ?? ((h) => clearInterval(h));
    this.setTimeoutFn = opts.setTimeoutFn ?? ((fn, ms) => setTimeout(fn, ms));
    this.clearTimeoutFn = opts.clearTimeoutFn ?? ((h) => clearTimeout(h));
    this.estimator = new ClockSyncEstimator({ now: this.now });
    this.keepAliveMs = opts.keepAliveMs ?? 25_000;
    this.scheduleKeepAlive =
      opts.scheduleKeepAlive ??
      ((fn, ms) => {
        const h = setInterval(fn, ms);
        return () => clearInterval(h);
      });
    // A version gap the store detects → ask the server for a full resync.
    opts.store.setResyncHandler(() => this.sendHello());
    // #247: a hidden→visible / background→foreground transition throttled the sync timers
    // → distrust the estimate until fresh samples arrive. Visibility is a PLATFORM concern
    // (browser `visibilitychange`, RN `AppState`), so the default here is a no-op and each
    // client injects its own `registerVisibility` (the web client wires `document`).
    const register = opts.registerVisibility ?? (() => () => {});
    this.unregisterVisibility = register(() => this.estimator.markSuspect());
  }

  /** Open the socket and begin streaming. */
  connect(): void {
    this.closedByUs = false;
    const factory = this.opts.socketFactory ?? defaultSocketFactory;
    const socket = factory(this.opts.url);
    this.socket = socket;

    socket.onopen = () => {
      // If this is a RECONNECT (attempts>0 means we backed off + retried), notify the
      // injected reconnect port before we reset the counter (EI-4 / #35, §3.4).
      // Fire-and-forget: the web client wires it to RUM (a silent no-op when
      // uninitialized); native / tests pass nothing, so this is safe everywhere. #247: a
      // reconnect opens a new socket / possibly a new network path → distrust the sync
      // estimate until fresh samples arrive (the old loop was stopped on close; the server
      // re-sends `sync_config` on the fresh connection if telemetry is still enabled).
      if (this.reconnectAttempts > 0) {
        this.opts.onReconnect?.({ attempt: this.reconnectAttempts });
        this.estimator.markSuspect();
      }
      this.reconnectAttempts = 0;
      this.lastAckedOpenClueId = null;
      // #264: a (re)connect invalidates any pending local scheduled open — the fresh socket
      // re-syncs and the server re-announces if a schedule is still live.
      this.clearScheduledOpen();
      this.opts.onOpenChange?.(true);
      // Handshake: hello triggers a full `game_state` at the current version.
      this.sendHello();
      // Keep an idle socket alive so the ALB (60s idle timeout) doesn't drop it before a
      // server-driven change arrives (e.g. the #246 demo box). Cleared on close/reconnect.
      this.startKeepAlive();
    };
    socket.onmessage = (ev) => {
      let parsed: ServerEvent;
      try {
        parsed = JSON.parse(String(ev.data)) as ServerEvent;
      } catch {
        return; // ignore non-JSON frames
      }
      // #247: handle the clock-sync control events before folding into the store (the
      // store ignores them). Then, after applying state, ack a fresh buzzer open.
      this.handleSyncEvent(parsed);
      this.opts.store.apply(parsed);
      this.maybeAckBuzzerOpen();
    };
    socket.onclose = () => {
      this.stopKeepAlive();
      this.opts.onOpenChange?.(false);
      // #247: a dropped socket stops the sync loop; it restarts on the next `sync_config`.
      this.stopSyncLoop();
      if (!this.closedByUs) this.scheduleReconnectWithBackoff();
    };
    socket.onerror = () => {
      // A failed socket will also fire onclose; reconnect is handled there.
    };
  }

  /** Send a command if the socket is open (dropped otherwise; a resync follows). */
  send(command: Command): void {
    if (this.socket && this.socket.readyState === OPEN) {
      this.socket.send(JSON.stringify(command));
    }
  }

  /** Close the socket and stop reconnecting. */
  close(): void {
    this.closedByUs = true;
    this.stopSyncLoop();
    this.clearScheduledOpen();
    this.unregisterVisibility?.();
    this.stopKeepAlive();
    this.socket?.close();
    this.socket = null;
  }

  // -------------------------------------------------------------------------
  // Clock-sync (#247, NFR-6.7) + scheduled fair open (#264, FR-4.13). Since #264 the sync loop
  // runs for every game in active play (D-264.1), not only observed ones — a warm-then-slow
  // cadence keeps idle-but-in-play sockets quiet (NFR-2.1).
  // -------------------------------------------------------------------------

  /**
   * Handle the two clock-sync control events. `sync_config` starts/stops the loop;
   * `time_sync_reply` completes a four-timestamp sample and reports it back so the SERVER
   * holds the authoritative offset. Everything else is left for the store.
   */
  private handleSyncEvent(event: ServerEvent): void {
    if (event.type === 'sync_config') {
      this.setSyncEnabled(event.enabled, event.intervalMs);
      return;
    }
    if (event.type === 'buzzer_open_scheduled') {
      this.handleScheduledOpen(event);
      return;
    }
    if (event.type === 'time_sync_reply') {
      const c2 = this.now();
      this.estimator.addSample({ c1: event.c1, s1: event.s1, s2: event.s2, c2 });
      this.send({
        type: 'time_sync_sample',
        c1: event.c1,
        s1: event.s1,
        s2: event.s2,
        c2,
      });
      // #264: a completed sample may have moved the estimate across the warm/steady tier —
      // re-arm the loop cadence if so (fast while untrustworthy, slow once A/B).
      this.maybeReArmSyncCadence();
    }
  }

  private setSyncEnabled(enabled: boolean, intervalMs: number): void {
    if (enabled) {
      this.syncEnabled = true;
      this.slowSyncIntervalMs = Math.max(1, intervalMs);
      if (!this.syncTimer) {
        // Fire one sample immediately so an estimate builds promptly, then pace at the
        // warm cadence (no samples yet → grade C) until the estimate is trustworthy.
        this.sendTimeSync();
        this.armSyncTimer(this.desiredSyncIntervalMs());
      }
    } else {
      this.stopSyncLoop();
    }
  }

  /** The cadence (ms) the loop should currently run at — warm while untrustworthy, else steady. */
  private desiredSyncIntervalMs(): number {
    const s = this.estimator.getState();
    const warming = s.suspect || s.quality === 'C';
    return warming
      ? Math.max(1, Math.min(this.slowSyncIntervalMs, WARM_SYNC_INTERVAL_MS))
      : Math.max(1, this.slowSyncIntervalMs);
  }

  /** (Re)arm the sync interval at `intervalMs`, replacing any current timer. */
  private armSyncTimer(intervalMs: number): void {
    if (this.syncTimer) this.clearIntervalFn(this.syncTimer);
    this.currentSyncIntervalMs = intervalMs;
    this.syncTimer = this.setIntervalFn(
      () => this.sendTimeSync(),
      Math.max(1, intervalMs),
    );
  }

  /** Re-arm the loop iff the desired cadence tier changed (warm↔steady). */
  private maybeReArmSyncCadence(): void {
    if (!this.syncEnabled || !this.syncTimer) return;
    const desired = this.desiredSyncIntervalMs();
    if (desired !== this.currentSyncIntervalMs) this.armSyncTimer(desired);
  }

  private stopSyncLoop(): void {
    this.syncEnabled = false;
    if (this.syncTimer) {
      this.clearIntervalFn(this.syncTimer);
      this.syncTimer = null;
    }
    this.currentSyncIntervalMs = 0;
  }

  private sendTimeSync(): void {
    this.send({ type: 'time_sync', c1: this.now() });
  }

  /**
   * #264 (FR-4.13): the current clock-sync estimate. Slice B's local buzzer-open scheduling
   * reads `offsetMs` to convert a server-time `OPEN_AT` into a local monotonic deadline
   * (`toClientTime`), and `quality`/`suspect`/`sampleCount` to decide whether to schedule or
   * fall back to an immediate open. Exposed here as the read seam.
   */
  getSyncState(): SyncState {
    return this.estimator.getState();
  }

  /**
   * #264 (FR-4.13): a scheduled fair open was announced. If this client holds a usable clock
   * estimate, convert the server `OPEN_AT` to a local monotonic deadline (never-early bias,
   * D-264.5) and arm a one-shot to unlock the buzzer locally at ~the shared instant — ahead of
   * the authoritative `buzzer_opened` frame's delivery. FALLBACK (D-264.8): no usable sync
   * (no samples / grade C / suspect) or the announcement arrived too late (deadline already
   * past) ⇒ don't schedule; the buzzer opens on `buzzer_opened` as before, and
   * {@link maybeAckBuzzerOpen} reports `scheduled:false`.
   */
  private handleScheduledOpen(event: BuzzerOpenScheduledEvent): void {
    // A new schedule supersedes any pending one (S5 mirror on the client).
    this.clearScheduledOpen();
    const state = this.estimator.getState();
    const usable = state.sampleCount > 0 && state.quality !== 'C' && !state.suspect;
    if (!usable) return; // fallback — open on the authoritative frame
    const safetyMargin = Math.max(
      0,
      Math.min(state.uncertaintyMs, MAX_SCHEDULED_OPEN_SAFETY_MARGIN_MS),
    );
    const localDeadline = toClientTime(event.openAtServerMonoMs, state) + safetyMargin;
    const delay = localDeadline - this.now();
    if (delay <= 0) return; // announcement too late → fallback (never open early, D-264.5)
    const clueId = event.clueId;
    this.scheduledOpenClueId = clueId;
    this.scheduledOpenTimer = this.setTimeoutFn(
      () => this.fireScheduledOpen(clueId),
      delay,
    );
  }

  /** #264: the local deadline elapsed — unlock the buzzer locally + ack `scheduled:true`. */
  private fireScheduledOpen(clueId: string): void {
    this.scheduledOpenTimer = null;
    const cs = this.opts.store.getSnapshot().state?.jeopardy.clueState;
    if (!cs || cs.clueId !== clueId) {
      // The clue changed/consumed while we waited — stale, drop it.
      this.clearScheduledOpen();
      return;
    }
    // Surface the local open to the UI (ORed into `canBuzz`, not `buzzerWindowOpen` — S4).
    this.scheduledOpenClueId = clueId;
    this.opts.onScheduledOpenChange?.(clueId);
    // Report the scheduled open (its normalized time vs OPEN_AT is the scheduled-open error).
    // Dedupe against the authoritative-open ack (`maybeAckBuzzerOpen`).
    if (this.syncEnabled && this.lastAckedOpenClueId !== clueId) {
      this.lastAckedOpenClueId = clueId;
      this.send({
        type: 'buzzer_open_ack',
        clueId,
        recvClientMonoMs: this.now(),
        scheduled: true,
      });
    }
  }

  /** #264: cancel + clear any pending/active local scheduled open (notifies the UI once). */
  private clearScheduledOpen(): void {
    if (this.scheduledOpenTimer) {
      this.clearTimeoutFn(this.scheduledOpenTimer);
      this.scheduledOpenTimer = null;
    }
    if (this.scheduledOpenClueId !== null) {
      this.scheduledOpenClueId = null;
      this.opts.onScheduledOpenChange?.(null);
    }
  }

  /**
   * Ack a freshly-opened buzzer once (advisory — lets the server measure cross-client open
   * delivery). Gated on an active sync session so idle games carry no extra traffic. #264: this
   * is the FALLBACK/authoritative-open path, so it reports `scheduled:false`; a client that
   * unlocked on the SCHEDULE already acked `scheduled:true` in {@link fireScheduledOpen} and is
   * deduped here by `lastAckedOpenClueId`.
   */
  private maybeAckBuzzerOpen(): void {
    if (!this.syncEnabled) return;
    const cs = this.opts.store.getSnapshot().state?.jeopardy.clueState;
    if (cs && cs.status === 'BUZZER_OPEN' && cs.clueId !== this.lastAckedOpenClueId) {
      this.lastAckedOpenClueId = cs.clueId;
      this.send({
        type: 'buzzer_open_ack',
        clueId: cs.clueId,
        recvClientMonoMs: this.now(),
        scheduled: false,
      });
    }
  }

  /** Begin sending periodic `ping`s while connected (idempotent). */
  private startKeepAlive(): void {
    if (this.keepAliveMs <= 0) return;
    this.stopKeepAlive();
    this.cancelKeepAlive = this.scheduleKeepAlive(() => {
      this.send({ type: 'ping' });
    }, this.keepAliveMs);
  }

  /** Stop the keepalive interval (no-op if not running). */
  private stopKeepAlive(): void {
    if (this.cancelKeepAlive) {
      this.cancelKeepAlive();
      this.cancelKeepAlive = null;
    }
  }

  private sendHello(): void {
    const lastVersion = this.opts.store.getSnapshot().version;
    // #257 S3: advertise the game's mode + this client's mode-contract version so the server
    // can negotiate the per-mode sub-contract (D-S3.4). Defaults to JEOPARDY (the only mode).
    const modeId = this.opts.gameType ?? 'JEOPARDY';
    this.send({
      type: 'hello',
      protocolVersion: PROTOCOL_VERSION,
      modeId,
      modeVersion: JEOPARDY_MODE_VERSION,
      ...(lastVersion >= 0 ? { lastVersion } : {}),
    });
  }

  private scheduleReconnectWithBackoff(): void {
    this.reconnectAttempts += 1;
    const factor = Math.min(this.reconnectAttempts, 10);
    const delay = this.reconnectBaseMs * factor;
    this.scheduleReconnect(() => {
      if (!this.closedByUs) this.connect();
    }, delay);
  }
}

function defaultSocketFactory(url: string): SocketLike {
  // The global `WebSocket` exists in browsers AND React Native; a client on a platform
  // without one injects its own `socketFactory`.
  return new WebSocket(url) as unknown as SocketLike;
}
